import { io } from 'socket.io-client';
import { decodeToken } from '../utils/jwtUtils';
import config from '../config';

const backAppURL = config.SOCKET_URL;

// These values are socket.io v4's own defaults, so this is the same behaviour the
// non-logged-in branch used to get implicitly - just stated explicitly for everyone.
const socketOptions = {
  transports: ['websocket'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  randomizationFactor: 0.5
};

// Read the token when it is needed rather than once at module load - on the login
// page there is no token yet, and that stale read decided things for the whole session.
const getUser = () => {
  try {
    const token = JSON.parse(localStorage.getItem('callex-token'));
    return token ? decodeToken(token) : null;
  } catch (e) {
    return null;
  }
};

// Tells the server which user this socket belongs to so it joins the right room.
const identify = (s) => {
  const user = getUser();
  if (user?.user_id) {
    s.emit('identify', { user_id: user.user_id, type: user?.type });
  }
};

let lastDisconnectReason = null;

// Returns a detach function so the handlers can be removed from an outgoing socket
// without touching any listener a component registered on it.
const attachLifecycle = (s) => {
  const onConnect = () => {
    lastDisconnectReason = null;
    console.log('[socket] connected', s.id);
    // Re-identify on every (re)connect so the server re-joins this user's room —
    // without this, a reconnect gets a fresh socket.id with no room membership
    // until the next component-level identify, and emits in that window are lost.
    identify(s);
  };

  const onDisconnect = (reason) => {
    lastDisconnectReason = reason;
    console.warn('[socket] disconnected:', reason);
    // socket.io does NOT auto-reconnect when the SERVER closes the connection
    // (backend restart/deploy). Without this the client stays dead indefinitely
    // while everything else looks healthy: SIP keeps working over its own socket,
    // so calls still ring, but no call/status event ever arrives again.
    if (reason === 'io server disconnect' && getUser()) {
      s.connect();
    }
  };

  const onConnectError = (err) => console.warn('[socket] connect_error:', err?.message);
  // In socket.io v4 these live on the Manager, not on the socket — a plain
  // `socket.on('reconnect')` silently never fires.
  const onReconnect = (attempt) => console.log('[socket] reconnected after', attempt);
  const onReconnectError = (err) => console.warn('[socket] reconnect_error:', err?.message);

  s.on('connect', onConnect);
  s.on('disconnect', onDisconnect);
  s.on('connect_error', onConnectError);
  s.io.on('reconnect', onReconnect);
  s.io.on('reconnect_error', onReconnectError);

  return () => {
    s.off('connect', onConnect);
    s.off('disconnect', onDisconnect);
    s.off('connect_error', onConnectError);
    s.io.off('reconnect', onReconnect);
    s.io.off('reconnect_error', onReconnectError);
  };
};

let socket = io(backAppURL, socketOptions);
let detachLifecycle = attachLifecycle(socket);

export const initializeSocket = () => {
  // Detach our own handlers from the outgoing instance so a stale socket can no
  // longer re-identify and take this user's room away from the live one. Its
  // connection and every component listener on it are left exactly as before.
  if (detachLifecycle) detachLifecycle();
  socket = io(backAppURL, socketOptions);
  detachLifecycle = attachLifecycle(socket);
  return socket;
};

export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    console.log('Socket disconnected');
  }
  socket = null;
};

// A machine waking from sleep often does not fire `online`, and a socket dropped
// while the tab was hidden can stay down. Re-check when the tab becomes visible.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!socket || socket.connected) return;
    // Never fight a deliberate disconnect (logout) or reconnect without a session.
    if (lastDisconnectReason === 'io client disconnect' || !getUser()) return;
    console.log('[socket] tab visible and socket is down — reconnecting');
    socket.connect();
  });
}

export { socket };
